import React from "react"
import { Badge } from "@mantine/core"
import { Link } from "lucide-react"

const convertDateTimeToLocalTime = (dateTime?: Date | string) => {
    if (!dateTime) {
        return "";
    }

    const userTimezone = getUserTimezone();

    let utcDate: Date;

    if (typeof dateTime === 'string') {
        const utcString = dateTime.endsWith('Z') ? dateTime : dateTime + 'Z';
        utcDate = new Date(utcString);
    } else {
        utcDate = new Date(dateTime);
    }

    const formattedDate = utcDate.toLocaleString('en-AU', {
        timeZone: userTimezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).replace(' at ', ' ');

    return formattedDate;
}

const getUserTimezone = () => {
    return localStorage.getItem('userTimezone') ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const formatText = (text: string) => {
    const lines = text.split('<br/>');

    return lines.map((line, lineIndex) => {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        const formattedLine = parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}> {part.slice(2, -2)} </strong>;
            }
            return part;
        });

        return (
            <React.Fragment key={lineIndex} >
                {formattedLine}
                {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
        );
    });
};

const displaySourceName = (sourceName?: string | null, sourceUrl?: string | null) => {
    if (!sourceName || !sourceUrl) {
        return ''
    }

    return <Badge color="#8a6a4a" variant="light" size="md" title={sourceUrl} style={{
        backgroundColor: '#8a6a4a1a',
        color: '#8a6a4a',
        border: '1px solid #8a6a4a1a',
        cursor: 'pointer',
        fontWeight: 600,
    }}
        radius="xl" leftSection={<Link size={12} />} onClick={() => window.open(sourceUrl, '_blank')}>
        {sourceName}
    </Badge>
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const extractPdfInfo = (answer: string): { url?: string; pages: number[] } => {
  // 1) URL in the <a href="..."> from “Surse consultate”
  const url = answer.match(/<a\s+href="([^"]+)"/i)?.[1];

  // 2) Pages from “pagina/paginile/pag.” + numbers or ranges
  const pages: number[] = [];
  const pagesSection = answer.match(
    /(pag(?:\.|ina|ini|inile|inilor)?\s*)([\d,\s\-–]+)/i
  )?.[2];

  if (pagesSection) {
    pagesSection
      .split(',')
      .map((s) => s.trim())
      .forEach((token) => {
        const range = token.match(/^(\d+)\s*[–-]\s*(\d+)$/);
        if (range) {
          const a = parseInt(range[1], 10), b = parseInt(range[2], 10);
          for (let p = Math.min(a, b); p <= Math.max(a, b); p++) pages.push(p);
        } else {
          const n = parseInt(token, 10);
          if (!Number.isNaN(n)) pages.push(n);
        }
      });
  }
  return { url, pages: Array.from(new Set(pages)) };
}

export { convertDateTimeToLocalTime, getUserTimezone, formatText, sleep, displaySourceName, extractPdfInfo };
